import React, { useEffect, useState, useCallback } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { Reorder } from "framer-motion";

import { AllstarUser, MontageSession } from "../../../../State";
import { Modal } from "../../Modal/Clips";
import { LoadingSkeleton } from "../../../../Components/Skeloton";
import { SelectClipCard } from "./Components/SelectClipCard";
import { Clip } from "./Components/Clip";
import { Count, CountIndicator } from "../CountIndicator";
import { TMontageClip, IClipsData } from "../../@types";
import { Tooltip } from "../../../../Components/Tooltip";
import { Body } from "../../../../Components/Text";
import { Grid } from "../../../../Components/Grid";
import { analytics } from "MVVM/Analytics";
export const countKills = (clips: TMontageClip[]) => {
  const count = clips
    .map((clip: TMontageClip) => {
      if (
        Array.isArray(clip.personalizations?.data?.kills) &&
        clip.personalizations?.data?.kills.length
      ) {
        return clip.personalizations.data.kills.length;
      }
      return (
        clip.timeline.filter((event) => {
          return event.event === "kill";
        }).length ?? 0
      );
    })
    .reduce((prev: number, next: number) => prev + next, 0);

  return count;
};

interface TClipsProps extends IClipsData {
  setModal: (modal: { body: string; isOpen: boolean }) => void;
}

type TClipSelectorModalState = {
  isOpen: boolean;
  insertIndex: number;
};

const StyledReorder = styled(Reorder.Group)<any>`
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const MAX_CLIPS = 10;

export const Clips = ({
  clipsLoading,
  clipsData,
  getClips,
  setModal,
}: TClipsProps) => {
  const allstarUser = useRecoilValue(AllstarUser);
  const [montageSession, setMontageSession] = useRecoilState(MontageSession);
  const [clipSelectorModalState, setClipSelectorModalState] =
    useState<TClipSelectorModalState>({
      isOpen: false,
      insertIndex: 0,
    });

  const [loading, setLoading] = useState<boolean>(false);

  const openClipSelectorModal = ({ insertIndex }: { insertIndex: number }) => {
    setClipSelectorModalState({ isOpen: true, insertIndex });
  };
  const closeClipSelectorModal = () => {
    setClipSelectorModalState({ isOpen: false, insertIndex: 0 });
  };

  const canProceed = montageSession.clips.length >= MAX_CLIPS;

  useEffect(() => {
    analytics.track("Career Montage - Step", {
      step: "Clips",
    });
  }, []);

  const setItems = useCallback(
    (newOrder: TMontageClip[]) => {
      setMontageSession((prevSession) => ({
        ...prevSession,
        clips: newOrder,
      }));
    },
    [setMontageSession],
  );

  const queryClips = async (filter: string[], sort: string) => {
    setLoading(true);
    const safeFilter = filter.length ? filter : null;

    await getClips({
      variables: {
        sequence: "freeform",
        user: allstarUser.user._id,
        filter: safeFilter,
        sort: sort,
        game: montageSession.game,
      },
    });
    setLoading(false);
  };

  if (clipsLoading && !loading) return <Loading />;

  const hasClipDemoExpired = (clipId: string) => {
    return (
      clipsData?.createMontageClipsV2?.some(
        (x) => x.clip._id == clipId && x.demoExpired,
      ) || false
    );
  };

  const getSafeClips = (): TMontageClip[] => {
    if (!clipsData) return [];
    return clipsData.createMontageClipsV2?.map((x) => x.clip) ?? [];
  };

  return (
    <>
      <Modal
        isOpen={clipSelectorModalState.isOpen}
        closeClipSelectorModal={closeClipSelectorModal}
        clips={getSafeClips()}
        hasClipDemoExpired={hasClipDemoExpired}
        loading={loading}
        queryClips={queryClips}
        count={montageSession.kills}
        openProCTA={setModal}
        insertIndex={clipSelectorModalState.insertIndex}
      />
      <HeaderContainer>
        <TextContainer>
          <Title>First, lets pick and order your clips</Title>
          <Subtitle>Select up to 10 clips for your Career Montage!</Subtitle>
        </TextContainer>
        <Tooltip
          position={{
            top: "35px",
            right: "20px",
            padding: "7px",
          }}
          type="hover"
          theme="helpTooltip"
          hide={canProceed}
          tooltipContent={
            <>
              <Flex flexDirection={"column"}>
                <Box>
                  <Count
                    count={montageSession.clips.length}
                    max={MAX_CLIPS}
                    canProceed={canProceed}
                  />
                </Box>
                <Body>You need at least 2 clips to create a montage</Body>
              </Flex>
            </>
          }
        >
          <CountIndicator
            heading={"Clips"}
            count={montageSession.clips.length}
            max={MAX_CLIPS}
            canProceed={canProceed}
          />
        </Tooltip>
      </HeaderContainer>
      <ClipsContainer>
        <StyledReorder
          axis="y"
          values={montageSession.clips}
          onReorder={setItems}
          className="reorder-group"
          style={{
            listStyleType: "none",
            all: "unset",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "15px",
            marginBottom: montageSession.clips.length > 0 ? "20px" : "0px",
            overflowY: "scroll",
            flex: 1,
          }}
          layoutScroll
        >
          {montageSession.clips.map((clip: TMontageClip, index: number) => {
            return (
              <Reorder.Item
                key={clip._id}
                value={clip}
                as="div"
                style={{
                  listStyleType: "none",
                  padding: "0px",
                }}
              >
                <Clip
                  key={clip._id}
                  clip={clip}
                  index={index}
                  isFinale={index === montageSession.clips.length - 1}
                  willExceedClipLimit={false}
                  demoExpired={hasClipDemoExpired(clip._id)}
                  isSelected={false}
                  selected={[]}
                  handleSelect={() => {}}
                  setModal={setModal}
                  forceColumnDisplay={false}
                />
              </Reorder.Item>
            );
          })}
          {montageSession.clips.length === 0 && (
            <SelectClipCard
              openClipSelectorModal={() => {
                openClipSelectorModal({
                  insertIndex: montageSession.clips.length,
                });
              }}
            />
          )}
        </StyledReorder>
        {montageSession.clips.length > 0 && (
          <StickySelectClipCard
            openClipSelectorModal={() => {
              openClipSelectorModal({
                insertIndex: montageSession.clips.length,
              });
            }}
          />
        )}
      </ClipsContainer>
    </>
  );
};

const Loading = () => {
  return (
    <Flex
      justifyContent={"flex-start"}
      width="685px"
      pb={10}
      flexDirection="column"
    >
      <LoadingSkeleton
        width={"150px"}
        height={"28px"}
        containerClassName={"align-self-start"}
      />
      <Grid>
        {[1, 1, 1, 1].map((_, index: number) => {
          return (
            <Flex
              key={index}
              height="240px"
              width="320px"
              flexDirection="column"
              pt={3}
            >
              <LoadingSkeleton
                height="290px"
                width="320px"
                style={{ borderRadius: "14px" }}
                containerClassName="flex-grow-1"
              />
              <Flex pt={2}>
                <LoadingSkeleton
                  height="30px"
                  width="160px"
                  style={{ borderRadius: "8px" }}
                />
              </Flex>
            </Flex>
          );
        })}
      </Grid>
    </Flex>
  );
};

const HeaderContainer = styled(Flex)`
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const TextContainer = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  flex: 1;
`;

const Title = styled(Body)`
  color: var(--Ice-100, #f7fcfd);
  font-family: Barlow;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px; /* 127.273% */
  letter-spacing: 0.035px;
`;

const Subtitle = styled(Body)`
  color: var(--Shade-300, #8fa9af);
  font-family: Barlow;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  text-align: left;
`;

const ClipsContainer = styled(Flex)`
  flex-direction: column;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const StickySelectClipCard = styled(SelectClipCard)`
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin-top: 0;
`;
